/*!
 * liveProject.js — Oscilla Live on a project: live.html?project=flowstates
 * © 2026 Rob Canning — GPLv3
 *
 * The project's folder instead of _live for pictures, sounds, film and text
 * (text(cycle:"responses.txt") reads its text/), and its code in a session
 * of its own (#s=flowstates), read from the project's text/livecode.txt
 * each time the page opens and written back to it a second after each
 * change -- the file on the server's disk is the working copy, so it can be
 * edited outside the browser too: looked at every two seconds, an edit there
 * comes into the editor when nothing has been typed since the last save
 * (typed meanwhile: the typing wins, and is written over it).
 *
 * The score's console does the same for a project that asks for it
 * (preferences.json "liveConsoleFile": true, oscillaLive.js), in the same
 * buffer: the score and Live on one machine share the code.
 */

import { onBufferSaved, storeBuffer, readBuffer, bufferText, takeBufferText } from "./oscillaLive.js";
import { bufferNameOf } from "./liveSessions.js";
import { textHash } from "../system/textHash.js";

const FILE = "text/livecode.txt";
const WRITE_MS = 1000;
const LOOK_MS = 2000;

/** The project the address asks for (?project=), if its name is a folder's name; else null. */
export function projectFromAddress() {
  const p = new URLSearchParams(location.search).get("project");
  return p && /^[a-zA-Z0-9_-][a-zA-Z0-9 _-]*$/.test(p) && !/\s$/.test(p) ? p : null;
}

/** A project's session id: its name in lower-case letters and digits (4 to 16 of them). */
function sessionOf(project) {
  const id = project.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 16);
  return id.length >= 4 ? id : `${id}proj`.slice(0, 16);
}

const fileUrl = project => `/scores/${encodeURIComponent(project)}/${FILE}`;
const writeUrl = project => `/api/project/${encodeURIComponent(project)}/file`;

/** The file's new text -- and, when known, a fingerprint of the text it was made from (server.js keeps the file first if it says something else). */
function fileForm(text, base = null) {
  const form = new FormData();
  form.append("path", FILE);
  if (base != null) form.append("base", textHash(base));
  form.append("file", new Blob([text], { type: "text/plain" }), "livecode.txt");
  return form;
}

// The file's text as this browser last agreed with it (written there, or taken
// from it), kept beside the buffer: what a reload compares both sides against.
const agreedKey = name => `oscilla.livecodeAgreed.${name}`;
function readAgreed(name) { try { return localStorage.getItem(agreedKey(name)); } catch { return null; } }
function keepAgreed(name, text) { try { localStorage.setItem(agreedKey(name), text); } catch { /* private mode: compared as if new */ } }

/** This browser's text, kept beside the file when the file wins over it (both had changed). */
async function keepAside(project, text) {
  const d = new Date(), p = n => String(n).padStart(2, "0");
  const path = `text/livecode-browser-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.txt`;
  const form = new FormData();
  form.append("path", path);
  form.append("file", new Blob([text], { type: "text/plain" }), "livecode.txt");
  try {
    if ((await fetch(writeUrl(project), { method: "POST", body: form })).ok) console.info(`[Live] ${project}: this browser's text, changed as the file was, kept as ${path}`);
  } catch { /* the file's text is in the editor all the same */ }
}

let stopCurrent = () => {};
let current = null;

/** The project whose file is the buffer now, or null (versions(): oscillaLive.js). */
export const projectFileOf = () => current;

/** The file no longer the buffer's: a write still waiting is sent first. */
export function stopProjectFile() { stopCurrent(); stopCurrent = () => {}; current = null; }

/**
 * The project's code into its session's buffer (when the file is there), and
 * every change after written back to the file. The session's id.
 *
 * Opening, both sides are compared with the text they last agreed on
 * (keepAgreed): typed here since, and the file as it was -- typing a reload
 * cut off before it reached the disk -- this browser's text is written (the
 * file's copy kept in versions/ first). The file changed since (an edit on
 * disk, another machine, Claude) -- it is taken in; if this browser's text
 * had changed too, or there is no agreement to go by yet, that is kept
 * beside the file (text/livecode-browser-<when>.txt), not lost. Never by the
 * buffer's time: a browser stamps its buffer whenever it saves it, typed or
 * not, and an old text looked new. The server keeps a copy of the file as
 * each session opens, and now and then as it is written.
 */
export async function useProjectFile(project) {
  stopProjectFile();
  current = project;
  const id = sessionOf(project);
  const name = bufferNameOf(id);
  let last = null;
  const agreed = text => { last = text; keepAgreed(name, text); };
  // the file as this session finds it, kept (the server skips it when nothing changed)
  const keptCopy = fetch(`/api/text-versions/${encodeURIComponent(project)}/snapshot`, {
    method: "POST", headers: { "content-type": "application/json" },
    body: JSON.stringify({ path: FILE, now: 1 }),
  }).catch(() => { /* an older server, or none: no copy */ });
  try {
    const res = await fetch(fileUrl(project), { cache: "no-store" });
    if (res.ok) {
      const disk = await res.text();
      const here = readBuffer(name)?.content ?? null;
      const was = readAgreed(name);
      const typedHere = here != null && here !== disk && here !== (was ?? disk);
      const fileMoved = was == null || disk !== was;
      if (typedHere && !fileMoved) {
        // typed here, not yet in the file, and the file as it was: written now, its copy kept first
        last = disk;
        keptCopy.then(() => fetch(writeUrl(project), { method: "POST", body: fileForm(here, disk) }))
          .then(r => { if (r.ok) agreed(here); })
          .catch(() => {});
        console.info(`[Live] ${project}/${FILE}: typed here and not yet written -- written now`);
      } else {
        // the file is the code: this browser's own changes, if any, kept beside it
        if (typedHere) keepAside(project, here);
        agreed(disk);
        storeBuffer(name, disk);
      }
    }
  } catch { /* offline: the buffer as this browser kept it */ }

  let timer = 0, pending = null;
  const write = async text => {
    pending = null;
    try {
      const res = await fetch(writeUrl(project), { method: "POST", body: fileForm(text, last) });
      if (res.ok) agreed(text);
      else console.warn(`[Live] ${project}/${FILE} not written: HTTP ${res.status}`);
    } catch (e) { console.warn(`[Live] ${project}/${FILE} not written:`, e); }
  };
  const offSaved = onBufferSaved((n, text) => {
    if (n !== name || text === last) return;
    clearTimeout(timer);
    pending = text;
    timer = setTimeout(() => write(text), WRITE_MS);
  });
  // the page going (a reload, the tab shut): a write still waiting goes now
  const flush = () => {
    if (pending == null || pending === last) return;
    clearTimeout(timer);
    navigator.sendBeacon?.(writeUrl(project), fileForm(pending, last));
    pending = null;
  };
  addEventListener("pagehide", flush);

  // an edit on disk (another editor, another machine): into the editor, unless typed over meanwhile
  const look = setInterval(async () => {
    if (document.hidden || last == null) return;
    let text;
    try {
      const res = await fetch(fileUrl(project), { cache: "no-store" });
      if (!res.ok) return;
      text = await res.text();
    } catch { return; }
    if (text === last) return;
    if (bufferText() !== last || pending != null) return;     // typed since: that is written next
    agreed(text);
    if (!takeBufferText(name, text)) storeBuffer(name, text);
    console.info(`[Live] ${project}/${FILE} changed on disk: taken in`);
  }, LOOK_MS);

  stopCurrent = () => {
    if (pending != null && pending !== last) { clearTimeout(timer); write(pending); }
    clearInterval(look);
    offSaved();
    removeEventListener("pagehide", flush);
  };
  return id;
}
